import type { FormEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  NavLink,
  Navigate,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from 'react-router';
import {
  useMarkNoticeSeen,
  useMe,
  useOrg,
  useServerNotices,
  useSettings,
  useSignOut,
  useUpcoming,
  useUpdateSettings,
} from '../api/queries';
import { isNotSignedIn } from '../api/client';
import { useFreshBuild } from '../api/fresh-build';
import { NoticeBar, initialsOf, Modal, Icon, Picker } from '../ui';
import { settle } from '../ledger/acted-here';
import { until } from '../lib/time';
import { LEGAL_ORIGIN, LEGAL_PAGES } from '../lib/legal';
import { APP_VERSION } from '../lib/releases';
import { setTab } from './tab';
import { WhatsNew } from './WhatsNew';
import { FreeMonth } from './FreeMonth';

/** Pages that do not need a signed-in person. */
// ⚠ `/stop` is open on purpose: it is the link at the foot of every message,
// and somebody who wants these to stop is usually signed into nothing. The
// token in the URL is the authority, and it can only turn something off.
const OPEN_PATHS = ['/start', '/design', '/stop'];

/**
 * Where a person lands when they have just signed in and asked for nothing in
 * particular.
 *
 * Only the root, and the reason is a guest: somebody from another company who
 * was invited to a meeting has no calendar of their own and never will, and
 * Meetings, Open and Knowledge are pages they are entitled to — the first
 * version of this rule bounced them out of all three, which the suite caught.
 * Anybody who navigates somewhere has said what they want; only the person who
 * has said nothing yet is shown the two steps.
 */
const needsCalendar = (pathname: string): boolean => pathname === '/';

/**
 * The shell — board 01: one header, four places, one reserved notice slot,
 * identical on every page. 64 px, white, one hairline; it holds the four
 * places and nothing that belongs to a page.
 */
export function AppShell(): ReactElement {
  const location = useLocation();
  const meQuery = useMe();
  const open = OPEN_PATHS.some((p) => location.pathname.startsWith(p));
  // Above every early return below: a hook runs on every render or none.
  useFreshBuild();
  // Another page: what was closed on the last one moves to where it belongs
  // (`ledger/acted-here`).
  useEffect(() => settle(), [location.pathname]);

  // ⚠ The start page is told how a sign-in ended (`?signin=` from the api's
  // Google callback). The redirect used to drop it, so "Sign-in was cancelled"
  // never showed — and a personal address turned away (2026-09-26) must be told
  // why, or it looks like the button does nothing.
  const signin = new URLSearchParams(location.search).get('signin');
  const toStart = `/start?returnTo=${encodeURIComponent(location.pathname)}${signin ? `&signin=${encodeURIComponent(signin)}` : ''}`;

  if (!open && meQuery.isPending) return <ShellFrame />;
  if (!open && meQuery.isError && isNotSignedIn(meQuery.error)) {
    return <Navigate to={toStart} replace />;
  }
  // ⚠ Signed in with no calendar is the other half of "not started yet", and
  // until now nothing said so: a new person landed on Today, which is empty
  // by definition — no meetings are coming, because nothing is watching a
  // calendar — with the one thing to do buried in Settings (Vladimir,
  // 2026-09-20: "i just logged in with a new user, again without the calendar
  // connection, why we dont route them to the start page").
  //
  // The root alone — see `needsCalendar`. Everything else still opens: a
  // shared meeting, Settings to reconnect, and every page a guest from another
  // company is entitled to.
  if (
    !open &&
    meQuery.isSuccess &&
    !meQuery.data.calendar.connected &&
    needsCalendar(location.pathname)
  ) {
    return <Navigate to={toStart} replace />;
  }
  return (
    <ShellFrame signedIn={meQuery.isSuccess}>
      <>
        <Outlet />
        {/* ⚠ A new page opens at its top; Back returns to where you were on
            the one before. Without it the window kept the last page's
            scroll, and a link at the bottom of one page opened the next at
            its bottom (Vladimir, 2026-09-25). A tab or a filter changing is
            not a new page: those replace the address with
            `preventScrollReset` (lib/url-tab). */}
        <ScrollRestoration />
      </>
    </ShellFrame>
  );
}

function ShellFrame({
  children,
  signedIn = false,
}: {
  children?: ReactElement;
  signedIn?: boolean;
}): ReactElement {
  return (
    <>
      <header className="shell-header">
        <div className="shell-bar">
          <NavLink to="/" className="wordmark" aria-label="Ptitim home">
            <span className="mark">P</span>
            Ptitim
          </NavLink>
          {/* ⚠ These duplicate `PLACES` above, which feeds the narrow-screen
              select. Adding a place to one and not the other is a place that
              exists on a phone and not on a desktop — which is what happened
              to Topics on 2026-09-22. Change both. */}
          {/* People is not here: it moved into the account menu (Vladimir,
              2026-09-25: "move people tab into the profile dropdown"). */}
          <nav className="tabs" aria-label="Places">
            <Place to="/meetings">Meetings</Place>
            <Place to="/open">Open</Place>
            <Place to="/topics">Topics</Place>
            <Place to="/knowledge">Knowledge</Place>
          </nav>
          <PlaceSelect />
          {signedIn ? <RecordTimeZone /> : null}
          {signedIn ? <Keys /> : null}
          {signedIn ? <Tab /> : null}
          {signedIn ? <LiveChip /> : null}
          {signedIn ? <Search /> : null}
          {signedIn ? <Account /> : null}
        </div>
      </header>
      <NoticeBar>
        {signedIn ? (
          <>
            <FreeMonth />
            <ServerNotices />
          </>
        ) : null}
      </NoticeBar>
      <main className="shell-main">{children}</main>
      <ShellFooter />
      {signedIn ? <WhatsNew /> : null}
    </>
  );
}

/**
 * The foot of every page: the legal pages, where people look for them.
 *
 * ⚠ They must stay reachable while somebody is using the app — Art. 12(1)
 * wants the information "easily accessible". They were in the account menu
 * from 2026-09-20; Vladimir, 2026-09-24: *"move legal pages to the proper
 * footer"*.
 */
function ShellFooter(): ReactElement {
  return (
    <footer className="shell-footer">
      <span>© {new Date().getFullYear()} Ptitim</span>
      <nav aria-label="Legal">
        {LEGAL_PAGES.map(([label, path]) => (
          <a key={path} href={`${LEGAL_ORIGIN}${path}`} target="_blank" rel="noreferrer">
            {label}
          </a>
        ))}
      </nav>
    </footer>
  );
}

/**
 * The five places, and every page each one owns.
 *
 * ⚠ A place is not one path. Meetings holds two lists — meetings and the
 * series they repeat in — behind the switch at the top of both, and the header
 * went dark the moment somebody pressed Series: *"when in header meetings is
 * selected and I switch tab to series meetings in header is not selected
 * anymore"* (Vladimir, 2026-09-21). The rule is the switch: **a page that
 * carries the Meetings · Series switch is in the Meetings place.**
 *
 * Detail pages are deliberately not here. A meeting or a commitment is a
 * thing, reached from anywhere — Today, search, Open — and lighting a place it
 * was not opened from would be a lie about where you are.
 */
const PLACES: Array<{ to: string; label: string; owns: (pathname: string) => boolean }> = [
  { to: '/', label: 'Today', owns: (p) => p === '/' },
  {
    to: '/meetings',
    label: 'Meetings',
    // Channels are the third way of looking at meetings, beside Series
    // (Vladimir, 2026-09-24) — the same switch, so the same place.
    owns: (p) =>
      p.startsWith('/meetings') ||
      p.startsWith('/calendar') ||
      p.startsWith('/series') ||
      p.startsWith('/channels') ||
      p.startsWith('/ch/'),
  },
  { to: '/open', label: 'Open', owns: (p) => p.startsWith('/open') },
  // ⚠ Beside Knowledge, not inside it. What the company knows is settled;
  // a topic is a thread still moving, and they answer different questions
  // (PRD 52).
  { to: '/topics', label: 'Topics', owns: (p) => p.startsWith('/topics') || p.startsWith('/t/') },
  { to: '/knowledge', label: 'Knowledge', owns: (p) => p.startsWith('/knowledge') },
];

/** Under 860px the four places are one select — never a horizontal scroll. */
/** What to call a page that is not one of the five places, for the phone's menu. */
function elsewhere(pathname: string): string {
  if (pathname.startsWith('/people')) return 'People';
  if (pathname.startsWith('/release-notes')) return 'Release notes';
  if (pathname.startsWith('/templates')) return 'Templates';
  if (pathname.startsWith('/settings')) return 'Settings';
  if (pathname.startsWith('/analytics')) return 'Analytics';
  if (pathname.startsWith('/m/')) return 'Meeting';
  if (pathname.startsWith('/c/')) return 'Commitment';
  if (pathname.startsWith('/t/')) return 'Topic';
  if (pathname.startsWith('/ask') || pathname.startsWith('/answers')) return 'Ask';
  if (pathname.startsWith('/search')) return 'Search';
  if (pathname.startsWith('/start')) return 'Start';
  return 'Elsewhere';
}

function PlaceSelect(): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // ⚠ The same rule the header's own tabs use, from the same list — the phone
  // showing "Series" while the desktop showed nothing lit was two answers to
  // "where am I".
  const current = PLACES.find((place) => place.owns(pathname))?.to ?? '';
  return (
    <Picker
      aria-label="Places"
      wrapClassName="nav-select"
      value={current}
      onChange={(to) => navigate(to)}
      size="sm"
      options={[
        ...PLACES.map((place) => ({ value: place.to, label: place.label })),
        // Somewhere that is not one of the five: name it, rather than show a
        // row of dots that says the menu is broken.
        ...(current === '' ? [{ value: '', label: elsewhere(pathname) }] : []),
      ]}
    />
  );
}

/**
 * One of the places, lit whenever the page in front of you belongs to it —
 * which is what `owns` decides, never NavLink's own path match.
 */
function Place({ to, children }: { to: string; children: string }): ReactElement {
  const { pathname } = useLocation();
  const on = PLACES.find((place) => place.to === to)?.owns(pathname) ?? false;
  return (
    <NavLink to={to} className={`tab ${on ? 'on' : ''}`} aria-current={on ? 'page' : undefined}>
      {children}
    </NavLink>
  );
}

/** What is happening now, or next: one chip, only when there is one. */
function LiveChip(): ReactElement | null {
  const upcoming = useUpcoming(1);
  const rows = upcoming.data ?? [];
  const live = rows.find(
    (e) =>
      e.capture === 'RECORDING' ||
      e.capture === 'JOINING' ||
      e.capture === 'WAITING_ROOM' ||
      e.capture === 'PAUSED',
  );
  const next = rows.find(
    (e) => Date.parse(e.startsAt) > Date.now() && (e.plan === 'WILL_RECORD' || e.capture !== null),
  );
  const pick = live ?? next;
  if (!pick) return null;
  const word = live
    ? live.capture === 'RECORDING'
      ? 'recording'
      : live.capture === 'PAUSED'
        ? 'paused'
        : live.capture === 'WAITING_ROOM'
          ? 'at the door'
          : 'joining'
    : 'will record';
  // ⚠ Where this goes matters more than it looks. It used to be /meetings — a
  // list that deliberately does not contain a meeting still in progress, so
  // clicking "recording" lost the thing it was pointing at. Since PRD 44 every
  // meeting has a page from the moment it is on the calendar, so there is
  // always one to go to; a calendar block with no link has no meeting and no
  // chip either.
  if (!pick.meetingId) return null;
  return (
    <NavLink
      to={`/m/${pick.meetingId}`}
      className="live-chip"
      title={live ? 'The meeting happening now' : pick.title}
    >
      <span className={`dot ${live && live.capture === 'RECORDING' ? 'dot-live' : 'dot-green'}`} />
      <span className="truncate" style={{ maxWidth: 220 }}>
        {pick.title}
        {live ? '' : ` ${until(pick.startsAt)}`}
      </span>
      <span className="mono">{word}</span>
    </NavLink>
  );
}

function Search(): ReactElement {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest(
        'input, textarea, [contenteditable]',
      );
      if (e.key === '/' && !typing) {
        e.preventDefault();
        setOpen(true);
        window.setTimeout(() => ref.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = ref.current?.value.trim() ?? '';
    if (q.length >= 2) navigate(`/search?q=${encodeURIComponent(q)}`);
  };
  if (!open) {
    return (
      <button
        type="button"
        className="search-pill"
        onClick={() => setOpen(true)}
        aria-label="Search"
      >
        <Icon name="search" size={16} />
        <span className="label">Search</span>
        <span className="kbd">/</span>
      </button>
    );
  }
  return (
    <form className="shell-search" role="search" onSubmit={submit}>
      <input
        ref={ref}
        name="q"
        className="field"
        placeholder="Search everything"
        aria-label="Search"
        onBlur={(e) => !e.target.value && setOpen(false)}
      />
    </form>
  );
}

function Account(): ReactElement | null {
  const meQuery = useMe();
  const org = useOrg();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    const onOpen = () => setSheet(true);
    window.addEventListener('ptitim:shortcuts', onOpen);
    return () => window.removeEventListener('ptitim:shortcuts', onOpen);
  }, []);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!meQuery.data) return null;
  const who = meQuery.data;
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="avatar-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
      >
        {who.pictureUrl ? (
          <img src={who.pictureUrl} alt="" referrerPolicy="no-referrer" />
        ) : (
          initialsOf(who.name, who.email)
        )}
      </button>
      {open ? (
        <div className="float menu popover" role="menu" style={{ right: 0, minWidth: 240 }}>
          <div style={{ padding: '8px 10px 6px' }}>
            <div className="t-heading" style={{ fontSize: 14 }}>
              {who.name ?? who.email}
            </div>
            <div className="mono">
              {org.data
                ? `${org.data.myRole === 'OWNER' ? 'Owner' : org.data.myRole === 'ADMIN' ? 'Admin' : 'Member'} · ${org.data.name}`
                : who.email}
            </div>
          </div>
          <div className="menu-sep" />
          {[
            // A directory you look somebody up in, not a place you work in all
            // day — so here, not in the header (2026-09-25).
            ['People', '/people'],
            // The shapes a meeting can be written up in (PRD 61).
            ['Templates', '/templates'],
            ['Your settings', '/settings'],
            ['Organisation', '/settings/organisation'],
            // Read, not set — so here rather than among the settings; an
            // administrator's, like the numbers it shows.
            ...(org.data?.canAdminister ? [['Analytics', '/analytics']] : []),
          ].map(([label, to]) => (
            <button
              key={to}
              type="button"
              role="menuitem"
              className="menu-item"
              onClick={() => go(to)}
            >
              {label}
            </button>
          ))}
          <div className="menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => go('/release-notes')}
          >
            Release notes
            <span className="mono" style={{ marginLeft: 'auto' }}>
              {APP_VERSION}
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => signOut.mutate(undefined, { onSettled: () => navigate('/start') })}
          >
            Sign out
          </button>
        </div>
      ) : null}
      {sheet ? <ShortcutsSheet onClose={() => setSheet(false)} /> : null}
    </div>
  );
}

/** Every key the app answers to — and only those. */
function ShortcutsSheet({ onClose }: { onClose: () => void }): ReactElement {
  const rows: Array<[string, string]> = [
    ['Search everything', '/'],
    ['Meetings · Open · Knowledge', '1 2 3'],
    ['People', '4'],
    ['This sheet', '?'],
    ['Close anything open', 'esc'],
  ];
  return (
    <Modal title="Shortcuts" onClose={onClose}>
      <div className="rows">
        {rows.map(([what, key]) => (
          <div key={what} className="row dense" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <span className="grow">{what}</span>
            <span className="cluster" style={{ gap: 4 }}>
              {key.split(' ').map((k) => (
                <span key={k} className="kbd">
                  {k}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/**
 * The browser tab (board 25): "Recording · Harbor weekly" with a red dot while
 * a notetaker is in a call, "Ptitim — a meeting was missed" while a notice
 * about one is unread, "Ptitim" otherwise. Nothing counts what you have read.
 */
function Tab(): null {
  const upcoming = useUpcoming(1);
  const notices = useServerNotices();
  const live = (upcoming.data ?? []).find(
    (e) => e.capture === 'RECORDING' || e.capture === 'PAUSED',
  );
  const missed = (notices.data ?? []).some(
    (n) => n.seenAt === null && /admit|fail|miss|recorded/i.test(`${n.kind} ${n.body}`),
  );
  useEffect(() => {
    setTab(live ? 'recording' : missed ? 'missed' : 'plain', live?.title);
  }, [live, missed]);
  return null;
}

/** 1 2 3 4 go places, ? opens the sheet — never while typing. */
function Keys(): null {
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest(
        'input, textarea, select, [contenteditable]',
      );
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const to: Record<string, string> = {
        '1': '/meetings',
        '2': '/open',
        '3': '/knowledge',
        '4': '/people',
      };
      if (to[e.key]) {
        e.preventDefault();
        navigate(to[e.key]);
      } else if (e.key === '?') {
        e.preventDefault();
        window.dispatchEvent(new Event('ptitim:shortcuts'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
  return null;
}

/** The bot's own notices, from the server: waiting at the door, refused, gone. */
function ServerNotices(): ReactElement | null {
  const notices = useServerNotices();
  const seen = useMarkNoticeSeen();
  // A door notice is true for the five minutes the notetaker waits, not a day
  // later. The api withdraws it when the bot gets in or gives up; this is the
  // belt to that brace, for a notice raised before the api learned to.
  const DOOR_PATIENCE_MS = 10 * 60_000;
  const unseen = (notices.data ?? []).filter(
    (n) =>
      n.seenAt === null &&
      !(n.kind === 'bot.waiting_room' && Date.now() - Date.parse(n.createdAt) > DOOR_PATIENCE_MS),
  );
  if (unseen.length === 0) return null;
  return (
    <>
      {unseen.slice(0, 3).map((n) => (
        <div key={n.id} className="notice notice-attention" role="status">
          <span className="notice-glyph" aria-hidden="true">
            !
          </span>
          <span className="grow">{n.body}</span>
          {n.meetingId ? (
            <NavLink to={`/m/${n.meetingId}`} className="btn btn-sm">
              What happened
            </NavLink>
          ) : null}
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            aria-label="Dismiss"
            onClick={() => seen.mutate(n.id)}
          >
            <Icon name="close" size={13} />
          </button>
        </div>
      ))}
    </>
  );
}

/**
 * The person's time zone, as their browser has it — told to the server once
 * when it differs, so "8:00 on a workday" in Slack is THEIR 8:00 (PRD 59).
 */
function RecordTimeZone(): null {
  const settings = useSettings();
  const update = useUpdateSettings();
  const asked = useRef(false);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const known = settings.data?.timeZone;
  const loaded = settings.isSuccess;
  const { mutate } = update;
  useEffect(() => {
    if (!loaded || !zone || known === zone || asked.current) return;
    asked.current = true;
    mutate({ timeZone: zone });
  }, [loaded, known, zone, mutate]);
  return null;
}
